import type { LocalGpsPoint } from '../offline/gpsTrackStore';

export interface LiveTrackLikePoint {
  lat: number;
  lng: number;
  timestamp: string;
  /** Se arrastran del almacén durable para el detector de cambio de sentido.
   *  El rumbo no: `LocalGpsPoint` no lo guarda. */
  accuracy?: number;
  speed?: number;
}

/**
 * Rehidrata la traza viva con los puntos DURABLES de `gpsTrackStore`.
 *
 * Por qué existe: el hook de tracking arrancaba la traza viva vacía asumiendo
 * que "el tramo anterior ya quedó reflejado en `gpsPoints` del backend". Eso
 * es cierto online, pero **sin cobertura los puntos siguen en IndexedDB
 * esperando el flush**: si la tablet recargaba a media ruta, el operario
 * perdía de vista por dónde había ido. El dato estaba a salvo; la
 * representación no.
 *
 * Se fusiona en vez de reemplazar porque entre el montaje y la lectura
 * asíncrona del almacén el watcher ya puede haber capturado posiciones: esas
 * son las más recientes y no deben perderse.
 *
 * Dedupe por `timestamp` (misma clave que usa el mapa para cruzar traza viva
 * y puntos de backend) y orden cronológico, para que la polilínea salga
 * monótona igual que la del mapa.
 */
export function mergeStoredLiveTrack(
  current: LiveTrackLikePoint[],
  stored: LocalGpsPoint[],
  maxPoints: number
): LiveTrackLikePoint[] {
  if (stored.length === 0) return current;

  const seen = new Set(current.map((point) => point.timestamp));
  const seeded = stored
    .filter((point) => !seen.has(point.timestamp))
    .map((point) => ({
      lat: point.latitude,
      lng: point.longitude,
      timestamp: point.timestamp,
      accuracy: point.accuracy,
      speed: point.speed,
    }));
  if (seeded.length === 0) return current;

  return [...seeded, ...current]
    .sort(
      (a, b) =>
        new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
    )
    .slice(-maxPoints);
}

export default mergeStoredLiveTrack;
